// src/utils/tokenEncryption.js
// Client-side token encryption using Web Crypto API

/**
 * Generate an encryption key from a passphrase
 * Uses device-specific information to create a unique key
 * @returns {Promise<CryptoKey>}
 */
const generateKey = async () => {
  // Create a passphrase from device-specific information
  const deviceInfo = [
    navigator.userAgent,
    navigator.language,
    screen.width,
    screen.height,
    new Date().getTimezoneOffset(),
    'CARO_Portal_v1' // App-specific salt
  ].join('|');

  // Hash the device info to create a key
  const encoder = new TextEncoder();
  const data = encoder.encode(deviceInfo);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);

  // Import the hash as a key
  return await crypto.subtle.importKey(
    'raw',
    hashBuffer,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );
};

/**
 * Encrypt a token
 * @param {string} token - Token to encrypt
 * @returns {Promise<object>} - Object with encrypted data and IV
 */
export const encryptToken = async (token) => {
  try {
    if (!token) return null;

    // Check if Web Crypto API is available
    if (!window.crypto || !window.crypto.subtle) {
      console.warn('Web Crypto API not available, storing token unencrypted');
      return { encrypted: false, data: token };
    }

    // Generate encryption key
    const key = await generateKey();

    // Generate a random initialization vector
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // Encrypt the token
    const encoder = new TextEncoder();
    const encodedToken = encoder.encode(token);

    const encryptedData = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      encodedToken
    );

    // Convert to base64 for storage
    const encryptedArray = new Uint8Array(encryptedData);
    const encryptedBase64 = btoa(String.fromCharCode.apply(null, encryptedArray));
    const ivBase64 = btoa(String.fromCharCode.apply(null, iv));

    return {
      encrypted: true,
      data: encryptedBase64,
      iv: ivBase64,
      timestamp: new Date().toISOString()
    };
  } catch (error) {
    console.error('Encryption error:', error);
    // Fallback to unencrypted storage
    return { encrypted: false, data: token };
  }
};

/**
 * Decrypt a token
 * @param {object} encryptedData - Object with encrypted data and IV
 * @returns {Promise<string|null>} - Decrypted token
 */
export const decryptToken = async (encryptedData) => {
  try {
    if (!encryptedData) return null;

    // If data was not encrypted, return as-is
    if (!encryptedData.encrypted) {
      return encryptedData.data;
    }

    // Check if Web Crypto API is available
    if (!window.crypto || !window.crypto.subtle) {
      console.error('Web Crypto API not available, cannot decrypt token');
      return null;
    }

    // Generate decryption key (same process as encryption)
    const key = await generateKey();

    // Convert base64 back to Uint8Array
    const encryptedArray = new Uint8Array(
      atob(encryptedData.data).split('').map(char => char.charCodeAt(0))
    );
    const iv = new Uint8Array(
      atob(encryptedData.iv).split('').map(char => char.charCodeAt(0))
    );

    // Decrypt the token
    const decryptedData = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv
      },
      key,
      encryptedArray
    );

    // Convert back to string
    const decoder = new TextDecoder();
    return decoder.decode(decryptedData);
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

/**
 * Encrypt user data
 * @param {object} userData - User data to encrypt
 * @returns {Promise<object>}
 */
export const encryptUserData = async (userData) => {
  try {
    if (!userData) return null;

    const jsonString = JSON.stringify(userData);
    return await encryptToken(jsonString);
  } catch (error) {
    console.error('User data encryption error:', error);
    return { encrypted: false, data: userData };
  }
};

/**
 * Decrypt user data
 * @param {object} encryptedData - Encrypted user data
 * @returns {Promise<object|null>}
 */
export const decryptUserData = async (encryptedData) => {
  try {
    if (!encryptedData) return null;

    const jsonString = await decryptToken(encryptedData);
    if (!jsonString) return null;

    if (!encryptedData.encrypted) {
      // Data was stored unencrypted
      return typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString;
    }

    return JSON.parse(jsonString);
  } catch (error) {
    console.error('User data decryption error:', error);
    return null;
  }
};

/**
 * Check if encryption is available
 * @returns {boolean}
 */
export const isEncryptionAvailable = () => {
  return !!(window.crypto && window.crypto.subtle);
};

/**
 * Test encryption/decryption
 * @returns {Promise<boolean>}
 */
export const testEncryption = async () => {
  try {
    const testData = 'test_token_12345';
    const encrypted = await encryptToken(testData);
    const decrypted = await decryptToken(encrypted);
    return decrypted === testData;
  } catch (error) {
    console.error('Encryption test failed:', error);
    return false;
  }
};

// Export default object with all methods
export default {
  encryptToken,
  decryptToken,
  encryptUserData,
  decryptUserData,
  isEncryptionAvailable,
  testEncryption
};
